/**
 * The wire format, mirroring backend/app/schemas.
 *
 * Hand-written rather than generated so the shapes stay readable, but they are
 * asserted by backend/tests/test_api_shape.py — if these drift, that suite is
 * where it shows up.
 */

export type TierId = "free" | "county" | "state";

/**
 * What an account *is*: one of the three tiers, or the one role that is not a
 * tier.
 *
 * Admin is deliberately outside `TierId` on both sides of the wire. It is
 * granted rather than bought — no price, no seat, and absent from the plan
 * table — so treating it as a fourth tier here would invite code that renders
 * it on the pricing page. See ADMIN_ROLE in backend/app/core/config.py.
 */
export type Role = TierId | "admin";

export interface Capabilities {
  can_filter: boolean;
  can_see_units: boolean;
  /** Whether saved alerts may be created. The routes refuse regardless. */
  can_watch: boolean;
  /** Whether rows carry their address and folio, or arrive redacted. */
  can_see_folio: boolean;
  /** null means unlimited. */
  max_buildings: number | null;
  /** Report records included each month on this plan. */
  report_credits: number;
  /**
   * How many of them are left. A capability and a fact about this account are
   * different things: "100 included" without "62 left" is a number the buyer
   * cannot act on.
   */
  credits_remaining: number;
}

export interface LegalStatus {
  accepted_version: string | null;
  accepted_at: string | null;
  current_version: string;
  /** True when the user has never accepted, or accepted an older version. */
  needs_acceptance: boolean;
}

/** Which counties an account may read. See backend/app/schemas/auth.py. */
export interface CountyEntitlement {
  /** null means every county the Service holds. */
  limit: number | null;
  /** The county a County plan covers; null on a plan that holds them all. */
  held: string | null;
  /** True when the plan is limited to one county and none is chosen yet. */
  must_choose: boolean;
  /** ISO timestamp the county may next be moved, or null to move it now. */
  next_change_at: string | null;
}

export interface User {
  email: string;
  /**
   * Who this account is. Served rather than derived client-side, so nothing
   * here has to re-implement "admin outranks tier" — getting that wrong once
   * shows an administrator an upgrade prompt, or shows an admin-only link to
   * someone the server will refuse.
   */
  role: Role;
  role_name: string;
  /** What they may *do*. An administrator's is `state`; their role is `admin`. */
  tier: TierId;
  tier_name: string;
  subscription_status: string | null;
  is_admin: boolean;
  capabilities: Capabilities;
  counties: CountyEntitlement;
  legal: LegalStatus;
}

/**
 * Which analytics and ad tags the page loads. Every field empty — the
 * default, and the state of any deployment with no campaign running — means
 * no third-party script is loaded at all. See lib/analytics.ts.
 */
export interface AnalyticsConfig {
  /** GA4 measurement id, "G-…". */
  ga_measurement_id: string;
  /** Google Ads tag, "AW-…", and the two conversion actions' labels. */
  google_ads_id: string;
  google_ads_signup_label: string;
  google_ads_purchase_label: string;
  /** Meta pixel id. */
  meta_pixel_id: string;
}

export interface AuthConfig {
  google_client_id: string;
  google_enabled: boolean;
  analytics: AnalyticsConfig;
  /**
   * Whether "Forgot password?" is offered at all.
   *
   * False on a deployment with no mail relay, where the link is not drawn —
   * the Google button's rule, for the same reason. A form that takes an
   * address and can never send anything leaves someone waiting for an email
   * instead of writing to support.
   */
  password_reset_enabled: boolean;
}

/** A request that changed something and has nothing to return but a sentence. */
export interface ActionResult {
  ok: boolean;
  detail: string;
}

export type LegalSlug = "terms" | "privacy" | "acceptable-use" | "data-licence";
export type GuideSlug = "scoring";
/** A guide and a legal document are the same shape on the wire. */
export type DocSlug = LegalSlug | GuideSlug;

export interface LegalDocumentSummary {
  slug: DocSlug;
  title: string;
  version: string;
  effective: string;
  summary: string;
}

export interface LegalDocument extends LegalDocumentSummary {
  /** Markdown. Rendered client-side; the server never emits HTML. */
  body: string;
}

export interface LegalIndex {
  agreement_version: string;
  documents: LegalDocumentSummary[];
}

export interface GuideIndex {
  guides: LegalDocumentSummary[];
}

export interface AuthResponse {
  token: string;
  user: User;
  /**
   * Whether this request created the account. Always true for signup; true
   * for Google sign-in only the first time. What the sign-up conversion is
   * fired on — nothing on the user record says which just happened.
   */
  created: boolean;
}

export interface Plan {
  id: TierId;
  name: string;
  price_usd: number;
  blurb: string;
  seat_cap: number | null;
  seats_left: number | null;
  can_filter: boolean;
  /** Report records included each month. Served, never written into the page. */
  report_credits: number;
  /**
   * Free days before the first charge, on an account's first subscription
   * only; 0 is no trial. Whether this reader still qualifies is checkout's
   * call, so the page words it as a first-subscription offer (`trialLine`).
   */
  trial_days: number;
}

export interface PlansResponse {
  plans: Plan[];
  billing_enabled: boolean;
}

/** One line of Stripe's proration arithmetic. Negative amounts are credits. */
export interface ProrationLine {
  amount_cents: number;
  description: string;
}

/** What switching plans costs, before anything is charged. */
export interface PlanChangePreview {
  held_tier: TierId;
  held_tier_name: string;
  tier: TierId;
  tier_name: string;
  direction: "upgrade" | "downgrade";
  currency: string;
  /** Taken now. Always 0 on a downgrade. */
  charged_today_cents: number;
  /** Unused time on the current plan, as a positive number. */
  credit_cents: number;
  /** What renewal comes to when the change settles there instead of now. */
  next_invoice_cents: number | null;
  renews_at: string | null;
  /**
   * The account is on a free trial, which is for one county: switching ends
   * it, and the new plan is charged in full today (`trialEndsOnSwitch`).
   */
  ends_trial: boolean;
  lines: ProrationLine[];
}

export interface PlanChangeResult {
  tier: TierId;
  tier_name: string;
  currency: string;
  charged_today_cents: number;
}

/** What we claim about a county, from the backend registry. */
export type CountyStatus = "live" | "planned" | "not_covered";

/**
 * One dated feed behind a county, and how old its newest record is.
 *
 * A frozen register passes every other check: it answers the query, publishes
 * its columns and returns rows. Fort Lauderdale's code register stopped in
 * 2019 and went on serving thousands of cases marked open, so the age of a
 * feed is part of what a county *is* rather than a footnote.
 *
 * `newest` null means the feed could not be dated — unmeasured, which is not
 * the same as current. Why is not served: it is a raw error for the operator.
 */
export interface SourceAge {
  label: string;
  newest: string | null;
  age_days: number | null;
  stale: boolean;
}

export interface County {
  /** Five-digit FIPS — the key the map's SVG paths carry too. */
  fips: string;
  name: string;
  status: CountyStatus;
  parcels: number;
  scored: number;
  buildings: number;
  milestone_overdue: number;
  /**
   * Signals this county publishes a feed for. A metric outside this list is
   * not zero there — it is unpublished, and the UI drops the tile rather than
   * showing a zero that reads as "none found".
   */
  signals: string[];
  /** The dated feeds behind this county. Empty where none publishes a date. */
  sources: SourceAge[];
}

export interface CountiesResponse {
  counties: County[];
  note: string;
}

export interface Jurisdiction {
  municipality: string;
  parcels: number;
  scored: number;
}

export interface CoverageResponse {
  jurisdictions: Jurisdiction[];
  note: string;
}

export interface Building {
  county_fips: string;
  parent_folio: string;
  building_score: number;
  address: string | null;
  city: string | null;
  year_built: number | null;
  age: number | null;
  derived_floors: number | null;
  total_units: number | null;
  distressed_units: number;
  distress_rate: number | null;
  unsafe_structure_cases: number;
  expired_permit_cases: number;
  lien_cases: number;
  absentee_units: number;
  /** Null where the record supports no determination — see milestone_status. */
  milestone_eligible: boolean | null;
  milestone_overdue: boolean;
  example_folio: string | null;
  milestone_status: MilestoneStatus;
  /** "county" = the county's own record. "estimated" = our inference. */
  milestone_source: MilestoneSource;
  recert_case: string | null;
  recert_year: number | null;
  recert_status: string | null;
  recert_enforcement: boolean;
  /**
   * Closed-case history, unscored. **Null is not zero.**
   *
   * Zero means the county register covers this row and holds nothing closed
   * for it; null means the register does not reach it — every county but
   * Miami-Dade, and every municipality inside Miami-Dade. See
   * `lib/closedCases.ts`, which is the only place allowed to word this.
   */
  closed_cases: number | null;
  median_days_to_close: number | null;
  /**
   * The SIRS special assessment the association reported to DBPR, on a
   * building carrying that signal: the study's completion date (ISO) and the
   * amounts in whole dollars, each null where the filing's figure was not
   * believable. All null where no report is joined — not "none was filed".
   * See `lib/sirsAssessment.ts`, the only place allowed to word it.
   */
  sirs_assessment_date: string | null;
  sirs_assessment_total: number | null;
  sirs_assessment_per_unit: number | null;
  /**
   * The managing association and its standing on Sunbiz — unscored context,
   * present only where the DBPR register reaches this building. See
   * `lib/association.ts`, the only place allowed to word it.
   */
  association: Association | null;
}

/** See `AssociationOut` on the server. `status` null is "no record joined". */
export interface Association {
  name: string;
  corporate_name: string | null;
  document_number: string | null;
  status: "active" | "inactive" | null;
  filed: string | null;
  last_event: string | null;
  successor: string | null;
  ambiguous: boolean;
  project_name: string | null;
  project_number: string | null;
  checked: string | null;
}

export type MilestoneSource = "county" | "estimated";

export type MilestoneStatus =
  | "county_enforcement"
  | "county_in_progress"
  | "county_current"
  | "county_cycle_due"
  | "estimated_overdue"
  | "estimated_eligible"
  | "estimated_unknown"
  | "not_eligible";

export interface Unit {
  county_fips: string;
  folio: string;
  score: number;
  flags: string[];
  site_addr: string | null;
  unit: string | null;
  city: string | null;
  zip: string | null;
  owner: string | null;
  owner_state: string | null;
  owner_country: string | null;
  is_condo: boolean;
  parent_folio: string | null;
  year_built: number | null;
  floors: number | null;
  use: string | null;
  total_val: number | null;
  last_sale: string | null;
  last_price: number | null;
  open_violations: number;
  violation_types: string[];
  oldest_violation: string | null;
  liens: number;

  /** Valuation context. Not scored — see /methodology. */
  land_val: number | null;
  living_area: number | null;
  value_per_sqft: number | null;
  /** Share of assessed value that is land. High = redevelopment candidate. */
  land_ratio: number | null;
  held_years: number | null;
  /**
   * Closed-case history, unscored. **Null is not zero.**
   *
   * Zero means the county register covers this row and holds nothing closed
   * for it; null means the register does not reach it — every county but
   * Miami-Dade, and every municipality inside Miami-Dade. See
   * `lib/closedCases.ts`, which is the only place allowed to word this.
   */
  closed_cases: number | null;
  median_days_to_close: number | null;
  /**
   * The most recent permit in the county's rolling 24-month window, and
   * whether it postdates the newest open case here.
   *
   * **Permitted work, not a repair.** The register does not say what the work
   * is for. Null means no permit, no dated case, or no permit register in that
   * county — never that a comparison was made and failed, which is what
   * `false` means.
   */
  last_permit_at: string | null;
  permit_after_case: boolean | null;
  /**
   * Unpaid property taxes, as the county Tax Collector's list states them:
   * the unpaid tax years newest first, the total owed in whole dollars, and
   * the day the list was exported (`YYYY-MM-DD`). Empty and null where the
   * unit is not on the list we hold — "not listed", never "paid". See
   * `lib/taxDelinquency.ts`, which is the only place allowed to word this.
   */
  tax_years: number[];
  tax_owed: number | null;
  tax_as_of: string | null;
  /** Jurisdiction. Also the coverage caveat: this is the county feed. */
  municipality: string | null;
}

export interface ScoringSignal {
  flag: string;
  label: string;
  description: string;
  points: number;
}

export interface MilestoneRule {
  status: MilestoneStatus;
  label: string;
  points: number;
  /** True when this comes from the county's case file, not our estimate. */
  authoritative: boolean;
}

/**
 * One term in a *building's* score, which is not built from the unit weights.
 *
 * `building_weights` alone is enough to compute with and not enough to show:
 * its `lien` key is the signal called `lien_recorded` everywhere else, and
 * three of the five are counted once for every unit carrying them while the
 * milestone pair is added once for the building.
 */
export interface BuildingSignal {
  key: string;
  label: string;
  points: number;
  /** True where the points are counted once per unit rather than once. */
  per_unit: boolean;
}

export interface ScoringRules {
  signals: ScoringSignal[];
  milestone: MilestoneRule[];
  building_signals: BuildingSignal[];
  building_weights: Record<string, number>;
  teardown_land_ratio: number;
  caveat: string;
}

/** Above this, the building contributes little to what the parcel is worth. */
export const TEARDOWN_LAND_RATIO = 0.7;

export interface Page {
  limit: number;
  offset: number;
  total: number;
  /** True when the tier limits what was returned. */
  capped: boolean;
  /** The unrestricted count — present only when capped. */
  true_total: number | null;
}

export interface Paged<T> {
  rows: T[];
  page: Page;
}

export interface HeadlineStats {
  overdue: number;
  buildings: number;
  units: number;
  unsafe: number;
  homestead_lost: number;
  liens: number;
}

export interface Status {
  /** The county the stats count, or null for every county held. */
  county: string | null;
  finished_at: string | null;
  unit_count: number;
  building_count: number;
  running: boolean;
  progress: string | null;
  last_error: string | null;
  stale: boolean;
  mode: string;
  stats: HeadlineStats;
  /**
   * How old each dated feed behind this county is. `stale` above is about our
   * own pull; these are about whether the county is still writing to what we
   * pull, and the two are independent — a register nobody has touched since
   * 2019 is fetched perfectly every morning.
   */
  sources: SourceAge[];
}

/** Stable error codes from backend/app/core/errors.py. */
export type ErrorCode =
  | "unauthenticated"
  | "no_token"
  | "invalid_token"
  /** The account signed in elsewhere; this device's token is no longer valid. */
  | "session_superseded"
  | "bad_credentials"
  | "forbidden"
  | "not_admin"
  | "upgrade_required"
  | "rate_limited"
  | "seats_full"
  | "email_taken"
  | "weak_password"
  | "invalid_email"
  | "billing_disabled"
  | "not_found"
  | "empty_export"
  | "invalid_request"
  /** The reset link is expired, already spent, or was never real. */
  | "invalid_reset_token"
  /** This deployment has no mail path, so reset cannot be offered. */
  | "reset_unavailable"
  /**
   * Three reports are already waiting on payment.
   *
   * A report is built before it is charged for, so an account with a pile of
   * unpaid ones has had a pile of documents rendered for it. The way out is in
   * the archive on the same page: buy one or delete one.
   */
  | "too_many_held"
  /** No document was stored, so there is nothing to charge for. */
  | "pdf_missing"
  | (string & {});

// ---------------------------------------------------------------------------
// Saved alerts
// ---------------------------------------------------------------------------

/** The filter set a watch carries, as stored. Deliberately partial: a watch
 *  saved before a filter existed simply does not have that key. */
export interface WatchFilters {
  milestone_only?: boolean;
  unsafe_only?: boolean;
  min_score?: number;
  min_rate?: number;
  flag?: string;
  search?: string;
  order?: string;
}

export interface Watch {
  id: number;
  name: string;
  scope: "buildings" | "units";
  county_fips: string;
  filters: WatchFilters;
  active: boolean;
  created_at: string | null;
  /** What "new" is measured from. */
  acked_at: string | null;
}

export interface WatchSummary {
  watch: Watch;
  new_count: number;
  /** False when the plan no longer covers the county this watch names. */
  entitled: boolean;
  reason: string;
}

export interface WatchList {
  watches: WatchSummary[];
  total_new: number;
}

export interface WatchMatches {
  watch: Watch;
  entitled: boolean;
  reason: string;
  buildings: Building[];
  units: Unit[];
  total: number;
  /** True when more matched than were returned. */
  capped: boolean;
}

/* --------------------------------------------------------------------------
   The operator console. Everything below is served only to the admin role —
   see backend/app/api/routes/admin.py, where the gate is on the router rather
   than on each route.
   -------------------------------------------------------------------------- */

/** One live county's own freshness, in the order the sweep will pull it. */
export interface FeedHealth {
  label: string;
  date_field: string;
  /**
   * The newest record on this feed. null where it could not be dated, in which
   * case `note` says why — never a date we are unsure of.
   */
  newest: string | null;
  /** Derived server-side on read, so it keeps counting up between pulls. */
  age_days: number | null;
  /**
   * Past this feed's own threshold. false when unmeasured: a service we could
   * not reach is fine as far as we know, and saying otherwise is an accusation
   * rather than a measurement.
   */
  stale: boolean;
  note: string;
  checked_at: string | null;
}

/**
 * A county whose latest pull the engine's collapse guard turned away: what it
 * answered beside what is still held. The engine kept the held copy.
 */
export interface EngineRefusal {
  fips: string;
  name: string;
  units: number | null;
  buildings: number | null;
  held_units: number;
  held_buildings: number;
  at: string | null;
}

export interface CountyHealth {
  fips: string;
  name: string;
  unit_count: number;
  building_count: number;
  /**
   * This county's last successful pull — not the run's. null means we have no
   * record of one ever finishing, which is a different statement from "it is
   * old" and the one that went unnoticed when Lee shipped holding nothing.
   */
  finished_at: string | null;
  stale: boolean;
  running: boolean;
  progress: string | null;
  /** What went wrong in the running process. Cleared by a restart. */
  last_error: string | null;
  /** Whether the last logged run for this county succeeded. Survives a restart. */
  last_run_ok: boolean | null;
  last_run_at: string | null;
  /**
   * This county's dated feeds. Empty for most counties, and that means "no
   * feed here can be dated" rather than "this county's feeds are current".
   */
  feeds: FeedHealth[];
  /** Set when the engine refused this county's latest pull as a collapse. */
  engine_refusal?: EngineRefusal | null;
}

export interface AccountSummary {
  total: number;
  /** Keyed by role. Every role is present, at zero if nobody holds it. */
  by_role: Record<Role, number>;
  awaiting_agreement: number;
  agreement_version: string;
}

/** The county an engine sweep is on now. */
export interface PullingCounty {
  fips: string;
  name: string;
  /** The engine's own line for it, e.g. "okaloosa parcels: 3,200". */
  detail: string | null;
  /** null in the moment between the sweep reaching it and its pull starting. */
  elapsed_seconds: number | null;
  /** Its last successful full pull's length; null with nothing to go on. */
  expected_seconds: number | null;
}

/**
 * One sweep the engine is running. The counts are facts; `fraction` and
 * `remaining_seconds` are estimates from each county's last pull, weighted by
 * time, and `overrun` says the county under way has run past last time.
 */
export interface EngineRun {
  kind: "scheduled" | "requested";
  started_at: string | null;
  counties: number;
  done: number;
  /** Names of the counties that failed so far in this sweep. */
  failed: string[];
  current: PullingCounty | null;
  overrun: boolean;
  remaining_seconds: number | null;
  fraction: number;
}

export interface EnginePulls {
  /** false: the progress could not be read — not the same as idle. */
  reachable: boolean;
  runs: EngineRun[];
  /** Counties whose latest pull the engine's collapse guard refused. */
  refused?: EngineRefusal[];
}

export interface RefreshSettings {
  running: boolean;
  /**
   * An operator's pull is being watched: the app checks the engine every
   * twenty seconds, and the desk keeps polling, until it is copied.
   */
  watching: boolean;
  in_flight: string[];
  mode: string;
  refresh_at: string | null;
  refresh_tz: string;
  refresh_hours: number;
  refresh_on_boot: boolean;
  /** Where county data is synced from; a private hostname. */
  engine_url: string | null;
  engine_configured: boolean;
  /** The engine release whose scoring constants are in force. */
  catalog_version: string | null;
  /** Which county the sweep takes next, and in what order after that. */
  pull_order: string[];
}

export interface AdminOverview {
  counties: CountyHealth[];
  accounts: AccountSummary;
  refresh: RefreshSettings;
  /** What the engine is pulling now — the pull itself, not the copy after. */
  pulls: EnginePulls;
}

export interface AdminUser {
  id: number;
  email: string;
  role: Role;
  role_name: string;
  /**
   * The stored tier, which differs from `role` for an administrator on
   * purpose: the column stays `free` so no sellable seat is consumed.
   */
  tier: TierId;
  subscription_status: string | null;
  agreement_version: string | null;
  created_at: string | null;
  last_login: string | null;
}

export interface AdminUserList {
  users: AdminUser[];
  /** Every account, not only the ones returned. */
  total: number;
  limit: number;
}

/** One row of the refresh log: one county, one run. */
export interface RefreshRun {
  id: number;
  county_fips: string | null;
  started_at: string | null;
  finished_at: string | null;
  ok: number;
  unit_count: number | null;
  bldg_count: number | null;
  note: string | null;
}

export interface RefreshTrigger {
  ok: boolean;
  started?: boolean;
  county?: string | null;
  note?: string;
}

/**
 * One record type in a report basket, with the arithmetic shown.
 *
 * Count, rate and subtotal all travel rather than only the total: this is the
 * screen where somebody decides to pay, and a single number asks to be trusted
 * where "1,204 parcels × $0.04 = $48.16" can be checked.
 */
export interface QuoteLine {
  kind: string;
  label: string;
  count: number;
  unit_cents: number;
  subtotal_cents: number;
  /**
   * What this line is, in the buyer's terms — why every building is charged
   * for, or which apartments sit above the cap and are not. Shown rather than
   * left to be inferred from a number smaller than the reader expected.
   */
  note: string;
  /**
   * Records delivered, where that differs from what is counted. The
   * apartments line delivers none: the buyer receives buildings, and the
   * apartments are what is inside them.
   */
  records: number;
}

export interface Quote {
  county_fips: string;
  county_name: string;
  /** The filters this was priced from, in words. */
  describes: string;
  lines: QuoteLine[];
  record_count: number;
  /**
   * Charged on every report that delivers something. An allowance never spends
   * itself on this — a credit is a record and the fee is not — but a basket
   * whose every record *is* covered comes to nothing at all, because the fee
   * is what makes a card transaction worth taking.
   */
  document_fee_cents: number;
  /**
   * What the lines come to, what the whole basket came to before the ceiling,
   * and what the ceiling took off. Separate so the arithmetic a buyer checks
   * still comes out — a total silently reduced is a sum that does not.
   */
  lines_total_cents: number;
  gross_cents: number;
  /** null where no ceiling is configured — the absent case, not a zero. */
  ceiling_cents: number | null;
  ceiling_discount_cents: number;
  /**
   * Apartments above the per-building cap and the buildings holding them. Not
   * a discount: the apartments line counts only what is charged for, so this
   * says what was left out rather than what was taken off.
   */
  capped_apartments: number;
  capped_buildings: number;
  subtotal_cents: number;
  /** Records, not cents — a credit is worth whatever record it covers. */
  credits_available: number;
  credits_applied: number;
  credit_value_cents: number;
  due_cents: number;
  /**
   * What this county can evidence **today**, so the buyer sees what they are
   * getting. Not the declared list: a declared signal whose every source reads
   * stale is in `excluded_signals` instead and was not counted.
   */
  signals: string[];
  /** The depth band it puts the county in, as a percentage of the rates. */
  band_percent: number;
  /**
   * Declared signals left out of the band because every feed supplying them
   * has stopped being written to. Named so the basket can say the band fell
   * and why, rather than quietly charging less.
   */
  excluded_signals: string[];
  /**
   * The most records one report may hold, and whether this basket is over it.
   * The server refuses an over-large report regardless; this is here so the
   * button can be refused before the click rather than after — which stops
   * being cosmetic the day a card is involved.
   */
  max_records: number;
  too_large: boolean;
  /**
   * True where this account is never charged. The lines still carry the real
   * price, so an internal account sees what a subscriber would be quoted
   * rather than a basket of zeroes.
   */
  waived: boolean;
  waived_reason: string;
}

/** The rate table. Served so the basket cannot hold its own copy. */
export interface Pricing {
  document_fee_cents: number;
  building_base_cents: number;
  affected_unit_cents: number;
  unit_cents: number;
  cap_apartments: number;
  /**
   * `[at most this many signals, percent of the rates]`, in order; a county
   * above the last threshold takes `depth_band_full`.
   */
  depth_bands: number[][];
  depth_band_full: number;
  report_ceiling_cents: number | null;
  labels: Record<string, string>;
}

/**
 * One report somebody generated, as the archive lists it.
 *
 * It carries no document. The list is read every time the page opens and the
 * files behind it are hundreds of kilobytes each, so `has_pdf` is what draws
 * the download control and the file has its own URL.
 */
export interface ReportRecord {
  id: number;
  county_fips: string;
  county_name: string;
  scope: string;
  /**
   * What was owed, and whether the document is released. `paid` is true where
   * nothing was owed at all — an internal or allowance-covered report — as
   * well as where a charge landed, because both mean the same thing to a
   * reader: this one is yours to download.
   *
   * `has_pdf` says a file was stored, never that it was bought. Both have to
   * be read before a download control is drawn.
   */
  due_cents: number;
  paid: boolean;
  /**
   * Where to go and pay. Present only on the response that minted a session —
   * creating the report, or asking to pay for a held one. Never on a listing:
   * a Checkout Session expires, so a stored one re-listed hours later would be
   * a dead link dressed as a button.
   */
  checkout_url: string;
  /**
   * When a held report stops being buyable and is swept. Null on anything
   * released — a report that is yours has no deadline.
   *
   * Absolute rather than a window, so the client never does the arithmetic: a
   * deadline computed in two places is one that disagrees with the sweep.
   */
  expires_at: string | null;
  /**
   * The filters it was built from, so a past report can be re-run as a new
   * one. Not a description of what the document contains — that is fixed, and
   * `describes` is the sentence printed on it.
   */
  filters: Record<string, unknown>;
  describes: string;
  record_count: number;
  building_count: number;
  unit_count: number;
  /**
   * What it cost on the day, in cents. Never recomputed from the current rate
   * table: a rate change must not restate the price of a report already held.
   */
  price_cents: number;
  credits_applied: number;
  waived: boolean;
  /**
   * Two dates, routinely days apart, and only the second says anything about
   * the data. Both are here for the reason the document prints both: a reader
   * shown one takes it for the other.
   */
  generated_at: string;
  pulled_at: string | null;
  pdf_bytes: number;
  /**
   * False where this was generated on a deployment with no PDF engine. The
   * record is real and the file is not — a state the list has to draw rather
   * than a row it should hide.
   */
  has_pdf: boolean;
}

export interface ReportsResponse {
  reports: ReportRecord[];
}
